import { useEffect, useRef, useState } from 'react'
import { pvToolApi } from '../../api/pvToolApi'
import { ApiError } from '../../api/httpClient'
import { MAX_ROOF_FACE_AREA_SQM, ROOF_ORIENTATIONS } from '../../dto/shared/SolarRequestDto'
import type { RoofFace, RoofOrientation } from '../../dto/shared/SolarRequestDto'
import type { PvSizingToolResponse } from '../../dto/shared/PvToolDto'
import { formatEuro, formatKwh, formatPercent } from '../../utils/format'
import { clamp, clampMin } from '../../utils/number'
import { triggerBlobDownload } from '../../utils/download'
import { track } from '../../analytics/track'
import { getAnonymousToolId } from '../../utils/anonymousToolId'
import { MonthlyEnergyChart } from './ui/MonthlyEnergyChart'
import { postalCodeMatchesCountry } from '../../utils/country'

interface PvSizingToolProps {
  /** Wired by the caller to whatever "start a real request" action makes sense there (e.g.
   *  useMarketingHeaderActions().onSelectCustomer on the standalone /pv-rechner page). */
  onRequestOffer: () => void
}

/** German-only labels, hardcoded like the rest of this German-only SEO tool page (see
 *  usePvRechnerSeo) - independent of the app's i18n enum translations, which track the UI
 *  language rather than this page's fixed German content. */
const ORIENTATION_LABELS_DE: Record<RoofOrientation, string> = {
  NORTH: 'Norden',
  NORTH_EAST: 'Nordosten',
  EAST: 'Osten',
  SOUTH_EAST: 'Südosten',
  SOUTH: 'Süden',
  SOUTH_WEST: 'Südwesten',
  WEST: 'Westen',
  NORTH_WEST: 'Nordwesten',
  FLAT: 'Flachdach',
}

// 80 m² entspricht ca. der durchschnittlichen nutzbaren Dachfläche eines Einfamilienhauses
// (roofora-issue-156), analog zum Default im eingeloggten Wizard (requestDraftModel.ts).
const DEFAULT_ROOF_FACE: RoofFace = { orientation: 'SOUTH', usableAreaSqm: 80, pitchDegrees: 35 }
const DEFAULT_ELECTRICITY_PRICE_CT = 32
const DEBOUNCE_MS = 400
const MAX_ROOF_FACES = 5

/** Free, anonymous, stateless PV sizing/economics estimator for the landing page - nothing here is
 *  ever persisted (calls only POST /tools/pv-sizing, never /requests). Mirrors the logged-in
 *  wizard's roof step (multiple faces, chart, PDF export) so the free tool feels like the real
 *  process (roofora-task-001), while staying stateless and login-free. */
export function PvSizingTool({ onRequestOffer }: PvSizingToolProps) {
  const [roofFaces, setRoofFaces] = useState<RoofFace[]>([DEFAULT_ROOF_FACE])
  const [postalCode, setPostalCode] = useState('')
  const [annualConsumptionKwh, setAnnualConsumptionKwh] = useState(4000)
  const [currentElectricityPriceCtPerKwh, setCurrentElectricityPriceCtPerKwh] = useState(DEFAULT_ELECTRICITY_PRICE_CT)
  const [hasElectricVehicle, setHasElectricVehicle] = useState(false)
  const [hasHeatPump, setHasHeatPump] = useState(false)

  const [result, setResult] = useState<PvSizingToolResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)
  // Fires once per mount, not on every debounced recalculation as the user tweaks inputs - same
  // reasoning as PvKostenRechnerWidget.tsx's trackedUsageRef.
  const trackedUsageRef = useRef(false)

  const usableAreaSqmTotal = roofFaces.reduce((sum, face) => sum + face.usableAreaSqm, 0)

  function addRoofFace() {
    setRoofFaces((prev) => [...prev, { orientation: 'SOUTH', usableAreaSqm: 10, pitchDegrees: 35 }])
  }

  function updateRoofFace(index: number, patch: Partial<RoofFace>) {
    setRoofFaces((prev) => prev.map((face, i) => (i === index ? { ...face, ...patch } : face)))
  }

  function removeRoofFace(index: number) {
    setRoofFaces((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev))
  }

  function buildRequest() {
    return {
      roofFaces,
      annualConsumptionKwh,
      hasElectricVehicle,
      hasHeatPump,
      batteryPreference: 'NO_PREFERENCE' as const,
      currentElectricityPriceCtPerKwh,
      // Omitted (not just trimmed) until it's a complete PLZ - a partial one typed so far (e.g.
      // "6", "61", "6138") reached the backend on every debounced keystroke and got forwarded
      // straight to the Zippopotam geocoder, which logged a 404 for every incomplete code.
      postalCode: postalCodeMatchesCountry(postalCode, 'DE') ? postalCode.trim() : undefined,
      anonymousId: getAnonymousToolId(),
    }
  }

  useEffect(() => {
    if (usableAreaSqmTotal <= 0 || annualConsumptionKwh <= 0 || currentElectricityPriceCtPerKwh <= 0) {
      setResult(null)
      return
    }

    setLoading(true)
    const timeout = setTimeout(() => {
      pvToolApi
        .calculate(buildRequest())
        .then((response) => {
          setResult(response)
          setError(null)
          if (!trackedUsageRef.current) {
            trackedUsageRef.current = true
            track('pv_rechner_used')
          }
        })
        .catch((err: unknown) => {
          setResult(null)
          setError(err instanceof ApiError ? err.message : 'Berechnung fehlgeschlagen.')
        })
        .finally(() => setLoading(false))
    }, DEBOUNCE_MS)

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roofFaces, postalCode, annualConsumptionKwh, hasElectricVehicle, hasHeatPump, currentElectricityPriceCtPerKwh])

  async function handleExportPdf() {
    setExportingPdf(true)
    try {
      const blob = await pvToolApi.downloadPdf(buildRequest())
      triggerBlobDownload(blob, 'roofora-pv-analyse.pdf')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'PDF-Export fehlgeschlagen.')
    } finally {
      setExportingPdf(false)
    }
  }

  return (
    <div className="pv-sizing-tool">
      <form className="pv-sizing-tool-form" onSubmit={(e) => e.preventDefault()}>
        <fieldset className="fieldset">
          <legend>Dachflächen</legend>
          {roofFaces.map((face, index) => (
            <div className="roof-face-row" key={index}>
              <select
                aria-label={`Dachausrichtung Fläche ${index + 1}`}
                value={face.orientation}
                onChange={(e) => updateRoofFace(index, { orientation: e.target.value as RoofOrientation })}
              >
                {ROOF_ORIENTATIONS.map((o) => (
                  <option key={o} value={o}>
                    {ORIENTATION_LABELS_DE[o]}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={1}
                max={MAX_ROOF_FACE_AREA_SQM}
                aria-label={`Nutzbare Dachfläche Fläche ${index + 1} (m²)`}
                value={face.usableAreaSqm}
                onChange={(e) => updateRoofFace(index, { usableAreaSqm: clamp(Number(e.target.value), 1, MAX_ROOF_FACE_AREA_SQM) })}
              />
              <span className="muted">m²</span>
              <input
                type="number"
                min={0}
                max={90}
                aria-label={`Dachneigung Fläche ${index + 1} (Grad)`}
                value={face.pitchDegrees ?? ''}
                onChange={(e) =>
                  updateRoofFace(index, {
                    pitchDegrees: e.target.value === '' ? null : clampMin(Number(e.target.value), 0),
                  })
                }
              />
              <span className="muted">°</span>
              {roofFaces.length > 1 && (
                <button type="button" className="btn-outline" onClick={() => removeRoofFace(index)}>
                  Entfernen
                </button>
              )}
            </div>
          ))}
          {roofFaces.length < MAX_ROOF_FACES && (
            <button type="button" className="btn-outline add-row-btn" onClick={addRoofFace}>
              + Weitere Dachfläche
            </button>
          )}
        </fieldset>

        <label>
          Postleitzahl (optional, für genaueren Monatsverlauf)
          <input
            type="text"
            inputMode="numeric"
            maxLength={10}
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
          />
        </label>

        <label>
          Jahresstromverbrauch (kWh)
          <input
            type="number"
            min={1}
            value={annualConsumptionKwh}
            onChange={(e) => setAnnualConsumptionKwh(clampMin(Number(e.target.value), 1))}
          />
        </label>

        <label>
          Aktueller Strompreis (ct/kWh)
          <input
            type="number"
            min={1}
            step={0.1}
            value={currentElectricityPriceCtPerKwh}
            onChange={(e) => setCurrentElectricityPriceCtPerKwh(clampMin(Number(e.target.value), 1))}
          />
        </label>

        <label className="pv-sizing-tool-checkbox">
          <input type="checkbox" checked={hasElectricVehicle} onChange={(e) => setHasElectricVehicle(e.target.checked)} />
          E-Auto vorhanden oder geplant
        </label>

        <label className="pv-sizing-tool-checkbox">
          <input type="checkbox" checked={hasHeatPump} onChange={(e) => setHasHeatPump(e.target.checked)} />
          Wärmepumpe vorhanden oder geplant
        </label>
      </form>

      {loading && <p className="pv-sizing-tool-status">Berechne…</p>}
      {error && (
        <div className="pv-sizing-tool-error-recovery">
          <p className="pv-sizing-tool-error">{error}</p>
          <p>Kein Problem - für ein individuelles Angebot mehrerer Handwerksbetriebe brauchst du diese Schnellschätzung nicht zwingend.</p>
          <button type="button" className="btn-primary" onClick={onRequestOffer}>
            Jetzt kostenloses Angebot einholen
          </button>
        </div>
      )}

      {result && (
        <div className="pv-sizing-tool-result">
          <ul className="kv-list">
            <li>
              <span>Empfohlene Anlagengröße</span>
              <strong>{result.sizing.systemSizeKwp.toLocaleString('de-DE')} kWp</strong>
            </li>
            <li>
              <span>Batteriespeicher</span>
              <strong>
                {result.sizing.batteryRecommended && result.sizing.batterySizeKwh
                  ? `${result.sizing.batterySizeKwh.toLocaleString('de-DE')} kWh empfohlen`
                  : 'Nicht nötig'}
              </strong>
            </li>
            <li>
              <span>Geschätzter Jahresertrag</span>
              <strong>{formatKwh(result.economics.annualYieldKwh)}</strong>
            </li>
            <li>
              <span>Geschätzte Ersparnis pro Jahr</span>
              <strong>{formatEuro(result.economics.annualBenefitEur)}</strong>
            </li>
            <li>
              <span>Geschätzte Investition</span>
              <strong>{formatEuro(result.economics.estimatedInvestmentEur)}</strong>
            </li>
            <li>
              <span>Amortisationszeit</span>
              <strong>{result.economics.paybackYears.toLocaleString('de-DE')} Jahre</strong>
            </li>
          </ul>

          {result.sizing.consumptionCoverageRatio < 0.98 && (
            // Selbe Warnung wie RequestSummaryPanel.tsx (eingeloggter Wizard) - ohne sie wirkte die
            // Empfehlung bei kleiner Dachfläche + hohem Verbrauch wie eine fehlerhafte Berechnung
            // statt einer durch die Dachfläche gedeckelten Teil-Abdeckung (Kunden-Bugreport Auftrag
            // #31, hier aber nie nachgezogen, weil dieses Tool result.sizing.consumptionCoverageRatio
            // bislang komplett ignorierte).
            <div className="warning-box" role="alert">
              Die angegebene Dachfläche deckt nur ca. {formatPercent(result.sizing.consumptionCoverageRatio)} des
              geschätzten Jahresverbrauchs. Für eine größere Anlage weitere Dachflächen ergänzen.
            </div>
          )}

          {result.economics.monthlyBreakdown.length > 0 && <MonthlyEnergyChart data={result.economics.monthlyBreakdown} />}

          <p className="pv-sizing-tool-disclaimer">
            Unverbindliche Grobschätzung ohne Speicherung deiner Angaben. Für ein individuelles Angebot mehrerer
            Handwerksbetriebe starte kostenlos eine Anfrage.
          </p>

          <div className="pv-sizing-tool-actions">
            <button type="button" className="btn-primary" onClick={onRequestOffer}>
              Jetzt kostenloses Angebot einholen
            </button>
            <button type="button" className="btn-outline" onClick={handleExportPdf} disabled={exportingPdf}>
              {exportingPdf ? 'PDF wird erstellt…' : 'Analyse als PDF herunterladen'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
